import { type FormEvent, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Markdown from "react-markdown";
import {
  issuesApi,
  issueTypeConfigsApi,
  type Issue,
  type IssueComment,
  type IssueStatus,
  type IssueType,
  type IssuePriority,
} from "../../api/issues.api";
import { usersApi } from "../../api/users.api";
import { useAuthStore } from "../../store/auth.store";
import { useAttachments, AttachmentsPanel } from "../../components/shared/AttachmentsPanel";

const STATUS_CLASS: Record<IssueStatus, string> = {
  OPEN: "success",
  IN_PROGRESS: "warning",
  UNDER_REVIEW: "warning",
  RESOLVED: "muted",
  CLOSED: "muted",
  WONT_FIX: "muted",
  ARCHIVED: "muted",
};

const STATUS_LABEL: Record<IssueStatus, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  UNDER_REVIEW: "Under Review",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  WONT_FIX: "Won't Fix",
  ARCHIVED: "Archived",
};

const PRIORITY_CLASS: Record<IssuePriority, string> = {
  LOW: "muted",
  MEDIUM: "warning",
  HIGH: "danger",
  CRITICAL: "danger",
};

function buildClaudePrompt(issue: Issue): string {
  const comments = issue.comments
    .filter((c) => !c.isInternal)
    .map((c) => {
      const author = c.author.displayName ?? c.author.email;
      const date = new Date(c.createdAt).toLocaleString();
      return `**${author}** (${date}):\n${c.body}`;
    })
    .join("\n\n");

  return `You are working on the SWFIC Admin Control Panel codebase at /home/user/admin_cp.
A user has reported the following issue. Please explore the relevant code, identify the root cause, and implement a fix.

## Issue: ${issue.title}
- **Type:** ${issue.type.replace(/_/g, " ")}
- **Priority:** ${issue.priority}
- **Status:** ${STATUS_LABEL[issue.status]}
- **Reported by:** ${issue.submittedBy.displayName ?? issue.submittedBy.email} (${issue.submittedBy.email})
- **App:** ${issue.app?.name ?? "N/A"}
- **Reported:** ${new Date(issue.createdAt).toLocaleString()}

## Description
${issue.body}
${comments ? `\n## Comments\n${comments}` : ""}

Please start by exploring the relevant files, then implement and test the fix.`;
}

function AdminPanel({ issue }: { issue: Issue }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: adminUsers } = useQuery({
    queryKey: ["users", "admins"],
    queryFn: () => usersApi.list({ panelAdminOnly: true }),
  });

  const { data: typeConfigs } = useQuery({
    queryKey: ["issue-type-configs"],
    queryFn: issueTypeConfigsApi.list,
  });
  const [copied, setCopied] = useState(false);

  const update = useMutation({
    mutationFn: (input: Parameters<typeof issuesApi.update>[1]) =>
      issuesApi.update(issue.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issue.id] });
      queryClient.invalidateQueries({ queryKey: ["issues"] });
    },
  });

  const archive = useMutation({
    mutationFn: () => issuesApi.update(issue.id, { status: "ARCHIVED" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues"] });
      navigate("/issues");
    },
  });

  function openInClaude() {
    const prompt = buildClaudePrompt(issue);
    navigator.clipboard.writeText(prompt).catch(() => undefined);
    window.location.href = "claude://";
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h4 style={{ marginTop: 0, marginBottom: 12 }}>Admin Controls</h4>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--muted)" }}>
            Status
          </label>
          <select
            value={issue.status}
            onChange={(e) => update.mutate({ status: e.target.value as IssueStatus })}
            disabled={update.isPending}
            style={{ width: "100%" }}
          >
            {(Object.keys(STATUS_LABEL) as IssueStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--muted)" }}>
            Type
          </label>
          <select
            value={issue.type}
            onChange={(e) => update.mutate({ type: e.target.value as IssueType })}
            disabled={update.isPending}
            style={{ width: "100%" }}
          >
            {(typeConfigs ?? []).map((cfg) => (
              <option key={cfg.type} value={cfg.type}>{cfg.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--muted)" }}>
            Priority
          </label>
          <select
            value={issue.priority}
            onChange={(e) => update.mutate({ priority: e.target.value as IssuePriority })}
            disabled={update.isPending}
            style={{ width: "100%" }}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4, color: "var(--muted)" }}>
            Assigned To
          </label>
          <select
            value={issue.assignedToId ?? ""}
            onChange={(e) => update.mutate({ assignedToId: e.target.value || null })}
            disabled={update.isPending}
            style={{ width: "100%" }}
          >
            <option value="">Unassigned</option>
            {adminUsers?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName ?? u.email}
              </option>
            ))}
          </select>
        </div>

        <div style={{ borderTop: "1px solid var(--border, #e2e8f0)", paddingTop: 12 }}>
          <button
            onClick={openInClaude}
            style={{ width: "100%", background: "var(--accent, #6366f1)", color: "#fff", border: "none" }}
          >
            {copied ? "✓ Prompt copied!" : "Open in Claude Code ↗"}
          </button>
          <p className="muted" style={{ fontSize: 11, marginTop: 6, marginBottom: 0 }}>
            Copies issue context to clipboard and opens Claude Code desktop app
          </p>
        </div>

        <div style={{ borderTop: "1px solid var(--border, #e2e8f0)", paddingTop: 12 }}>
          <button
            onClick={() => {
              if (confirm("Archive this issue?")) archive.mutate();
            }}
            disabled={archive.isPending || issue.status === "ARCHIVED"}
            style={{ width: "100%", color: "var(--danger, #e53e3e)" }}
          >
            {archive.isPending ? "Archiving…" : issue.status === "ARCHIVED" ? "Archived" : "Archive Issue"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({
  comment,
  isAdmin,
  currentUserId,
  issueId,
}: {
  comment: IssueComment;
  isAdmin: boolean;
  currentUserId: string | undefined;
  issueId: string;
}) {
  const queryClient = useQueryClient();
  const author = comment.author.displayName ?? comment.author.email;
  const date = new Date(comment.createdAt).toLocaleString();
  const canEdit = isAdmin || comment.authorId === currentUserId;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [draftInternal, setDraftInternal] = useState(comment.isInternal);

  const update = useMutation({
    mutationFn: () => issuesApi.updateComment(issueId, comment.id, draft, isAdmin ? draftInternal : undefined),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["issues", issueId] });
    },
  });

  return (
    <div
      style={{
        padding: "12px 0",
        borderBottom: "1px solid var(--border, #e2e8f0)",
        background: comment.isInternal ? "#1d4ed8" : "#3b82f6",
        borderRadius: 6,
        paddingLeft: 12,
        paddingRight: 12,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
        <strong style={{ fontSize: 13 }}>{author}</strong>
        <span className="muted" style={{ fontSize: 12 }}>{date}</span>
        {isAdmin && comment.isInternal && (
          <span className="badge muted" style={{ fontSize: 10 }}>Internal</span>
        )}
        {canEdit && !editing && (
          <button
            onClick={() => { setDraft(comment.body); setDraftInternal(comment.isInternal); setEditing(true); }}
            style={{ marginLeft: "auto", fontSize: 12, padding: "2px 8px", background: "rgba(0,0,0,0.2)", border: "none", borderRadius: 4, cursor: "pointer", color: "inherit" }}
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={5000}
            style={{ width: "100%", resize: "vertical", marginBottom: 6 }}
          />
          {update.isError && <p className="error-text" style={{ margin: "0 0 6px" }}>Failed to save.</p>}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="primary"
              onClick={() => update.mutate()}
              disabled={update.isPending || draft.trim().length === 0}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              {update.isPending ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={update.isPending}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              Cancel
            </button>
            {isAdmin && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginLeft: 4, color: "white", whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={draftInternal}
                  onChange={(e) => setDraftInternal(e.target.checked)}
                />
                Internal Comment (Admins Only)
              </label>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="prose">
            <Markdown
            components={{
              img: ({ src, alt }) => (
                <img src={src} alt={alt} style={{ maxWidth: "100%", borderRadius: 4, marginTop: 8 }} />
              ),
              p: ({ children }) => <p style={{ margin: "0 0 8px", fontSize: 14, whiteSpace: "pre-wrap" }}>{children}</p>,
              a: ({ href, children }) => <a href={href} className="prose-link" target="_blank" rel="noopener noreferrer">{children}</a>,
            }}
          >
            {comment.body}
          </Markdown>
          </div>
          {comment.editedAt && (
            <p style={{ margin: "6px 0 0", fontSize: 11, color: "#fca5a5" }}>
              {comment.editedBy?.displayName ?? comment.editedBy?.email ?? "Unknown"} Edited,{" "}
              {new Date(comment.editedAt).toLocaleString()}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function CommentForm({ issueId }: { issueId: string }) {
  const isAdmin = useAuthStore((s) => s.user?.isPanelAdmin === true);
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const attachments = useAttachments();

  const add = useMutation({
    mutationFn: () =>
      issuesApi.addComment(issueId, { body: attachments.buildMarkdown(body), isInternal }),
    onSuccess: () => {
      setBody("");
      setIsInternal(false);
      attachments.reset();
      queryClient.invalidateQueries({ queryKey: ["issues", issueId] });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    add.mutate();
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      <textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onPaste={attachments.handlePaste}
        placeholder="Add a comment…"
        rows={3}
        minLength={1}
        maxLength={5000}
        required
        style={{ width: "100%", resize: "vertical", marginBottom: 4 }}
      />
      <AttachmentsPanel
        files={attachments.files}
        setFiles={attachments.setFiles}
        anyUploading={attachments.anyUploading}
        processFile={attachments.processFile}
        removeFile={attachments.removeFile}
      />
      {add.isError && <p className="error-text">Failed to post comment.</p>}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
        <button type="submit" className="primary" disabled={add.isPending || attachments.anyUploading}>
          {add.isPending ? "Posting…" : "Post Comment"}
        </button>
        {isAdmin && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", color: "white" }}>
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
            />
            Internal Comment (Admins Only)
          </label>
        )}
      </div>
    </form>
  );
}

export function IssueDetail() {
  const { issueId } = useParams<{ issueId: string }>();
  const isAdmin = useAuthStore((s) => s.user?.isPanelAdmin === true);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();

  const { data: issue, isLoading, error } = useQuery({
    queryKey: ["issues", issueId],
    queryFn: () => issuesApi.get(issueId!),
    enabled: !!issueId,
  });

  const { data: typeConfigs } = useQuery({
    queryKey: ["issue-type-configs"],
    queryFn: issueTypeConfigsApi.list,
  });
  const typeLabelMap: Record<string, string> = Object.fromEntries(
    (typeConfigs ?? []).map((c) => [c.type, c.label])
  );

  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !issue) return <p className="error-text">Issue not found.</p>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: isAdmin ? "1fr 240px" : "1fr", gap: 24, alignItems: "start" }}>
      <div>
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => navigate("/issues")} style={{ marginBottom: 12, fontSize: 13 }}>
            ← Back to Report App Issues
          </button>
          <h2 style={{ margin: 0 }}>{issue.title}</h2>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <span className={`badge ${STATUS_CLASS[issue.status]}`}>{STATUS_LABEL[issue.status]}</span>
          <span className={`badge ${PRIORITY_CLASS[issue.priority]}`}>{issue.priority}</span>
          <span className="badge muted">{typeLabelMap[issue.type] ?? issue.type.replace(/_/g, " ")}</span>
          {issue.app && <span className="badge muted">{issue.app.name}</span>}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12, color: "var(--muted)" }}>
            <span>
              Reported by{" "}
              <strong>{issue.submittedBy.displayName ?? issue.submittedBy.email}</strong>
            </span>
            <span>·</span>
            <span>{new Date(issue.createdAt).toLocaleString()}</span>
            {issue.assignedTo && (
              <>
                <span>·</span>
                <span>
                  Assigned to <strong>{issue.assignedTo.displayName ?? issue.assignedTo.email}</strong>
                </span>
              </>
            )}
          </div>
          <div className="prose">
            <Markdown
              components={{
                img: ({ src, alt }) => (
                  <img src={src} alt={alt} style={{ maxWidth: "100%", borderRadius: 4, marginTop: 8 }} />
                ),
                p: ({ children }) => <p style={{ margin: "0 0 8px", whiteSpace: "pre-wrap" }}>{children}</p>,
                a: ({ href, children }) => <a href={href} className="prose-link" target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >
              {issue.body}
            </Markdown>
          </div>
        </div>

        <div className="card">
          <h4 style={{ marginTop: 0, marginBottom: 0 }}>
            Comments ({issue.comments.length})
          </h4>
          {issue.comments.length > 0 ? (
            issue.comments.map((c) => (
              <CommentItem key={c.id} comment={c} isAdmin={isAdmin} currentUserId={currentUserId} issueId={issue.id} />
            ))
          ) : (
            <p className="muted" style={{ marginBottom: 0, fontSize: 14 }}>No comments yet.</p>
          )}
          <CommentForm issueId={issue.id} />
        </div>
      </div>

      {isAdmin && (
        <div>
          <AdminPanel issue={issue} />
        </div>
      )}
    </div>
  );
}
